import { useMemo } from 'react';
import { useField, useForm } from 'react-final-form';

import { Button, Icons, Stack } from '@cast/design-system';
import { useRffFormState } from '@cast/shared-rff-ui';
import { notReachable } from '@cast/utils';

import { DEFAULT_RULE, FIELD_NAMES } from '../../_constants';
import type { BuilderRule, FormValues } from '../../_types';
import {
  mapBuilderRulesToYamlString,
  parseYamlToBuilderRules,
} from '../../_utils';

import { NonRepresentableConfig } from './NonRepresentableConfig';
import { NoRulesPlaceholder } from './NoRulesPlaceholder';
import { RuleRow } from './RuleRow';
import { validateRule } from '../../_utils/builder';

type BuilderViewState =
  | { type: 'nonRepresentable' }
  | { type: 'empty' }
  | {
      type: 'rules';
      rules: BuilderRule[];
    };

const getBuilderViewState = (
  advancedEvictorConfigFormValue: FormValues['advancedEvictorConfig']
): BuilderViewState => {
  const parseResult = parseYamlToBuilderRules(advancedEvictorConfigFormValue);

  if (!parseResult.success) {
    return { type: 'nonRepresentable' };
  }
  const rules = parseResult.value;

  if (rules.length === 0) {
    return { type: 'empty' };
  }

  return {
    type: 'rules',
    rules,
  };
};

export const ConfigurationBuilder = () => {
  const { mode: formMode } = useRffFormState();
  const disabled = formMode === 'view';

  const { input: advancedEvictorConfigInput } = useField<
    FormValues['advancedEvictorConfig']
  >(FIELD_NAMES.advancedEvictorConfig);

  const advancedEvictorConfigFormValue = advancedEvictorConfigInput.value;

  const form = useForm<FormValues>();

  const viewState = useMemo(
    () => getBuilderViewState(advancedEvictorConfigFormValue),
    [advancedEvictorConfigFormValue]
  );

  const handleRulesChange = (updatedRules: BuilderRule[]) => {
    form.change(
      FIELD_NAMES.advancedEvictorConfig,
      mapBuilderRulesToYamlString(updatedRules)
    );
  };

  switch (viewState.type) {
    case 'nonRepresentable':
      return <NonRepresentableConfig />;
    case 'empty':
      return (
        <NoRulesPlaceholder
          disabled={disabled}
          onAddRule={() => handleRulesChange([{ ...DEFAULT_RULE }])}
        />
      );
    case 'rules': {
      const rules = viewState.rules;
      const lastRule = rules.at(-1);
      const isLastRuleValid = !!lastRule && validateRule(lastRule).valid;

      return (
        <Stack gap={12} data-testid="evictor-advanced-configurations-builder">
          {rules.map((rule, index) => {
            return (
              <RuleRow
                key={index}
                rule={rule}
                disabled={disabled}
                onChange={(updatedRule) =>
                  handleRulesChange(
                    rules.map((existingRule, ruleIndex) =>
                      ruleIndex === index ? updatedRule : existingRule
                    )
                  )
                }
                onRemove={() =>
                  handleRulesChange(
                    rules.filter((_, ruleIndex) => ruleIndex !== index)
                  )
                }
              />
            );
          })}

          <Button
            variant="secondary"
            size="sm"
            startIcon={<Icons.Plus />}
            disabled={disabled || !isLastRuleValid}
            onClick={() => handleRulesChange([...rules, { ...DEFAULT_RULE }])}
            testId="evictor-add-rule-button"
          >
            Add rule
          </Button>
        </Stack>
      );
    }
    default:
      return notReachable(viewState);
  }
};
